import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { styled, keyframes } from 'styled-components';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { IconDesktop16MiscOverflowMenu } from '@fabric-app/icons/svg';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useFloating, inline, offset, autoPlacement, size, autoUpdate } from '@floating-ui/react';
import { createPortal } from 'react-dom';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useOnClickOutside } from '@fabric-app/hooks/useOnClickOutside';
import { mergeRefs } from 'react-merge-refs';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { useVirtualKeyboardContext } from '../../keyboard/keyboard.context.js';
import { isString } from '@fabric-app/utils/guards';
import { MenuItemInfoIconTooltip } from '@fabric-app/ui/components/Menu/MenuItemInfoIconTooltip';

const Anchor = styled.div.withConfig({
  displayName: "EditorSelector__Anchor",
  componentId: "sc-8b35be9-0"
})([
  `z-index:126;pointer-events:auto;`
]);
const FadeInScaleBottomRight = keyframes([
  `from{opacity:0;transform:scale(0.9) translateY(10px);}to{opacity:1;transform:scale(1) translateY(0);}`
]);
const DesktopContent = styled(DropdownMenu.Content).attrs((p) => {
  return {
    ...p,
    as: "div"
  };
}).withConfig({
  displayName: "EditorSelector__DesktopContent",
  componentId: "sc-8b35be9-1"
})([
  `width:204px;opacity:0;padding:0;position:relative;animation:`,
  ` 0.2s ease-out forwards;transform-origin:top left;overflow:hidden;max-height:min(var(--fbc-editor-selector-available-height),360px);background:`,
  `;box-shadow:0px 1px 13.5px 0px rgba(`,
  `,0.05);`
], FadeInScaleBottomRight, cssVar["color-bg-contrast"], cssVar["color-bg-contrast-reverse-rgb"]);
const MobileContent = styled.div.withConfig({
  displayName: "EditorSelector__MobileContent",
  componentId: "sc-8b35be9-2"
})([
  `display:flex;flex-direction:column;background:`,
  `;gap:1rem;overflow:hidden;max-height:`,
  `px;height:100%;min-height:200px;&:focus,&:focus-visible,&:focus-within{outline:none;}`
], cssVar["color-bg-primary"], ({ maxHeight }) => maxHeight || 360);
const StyledScrollArea = styled(ScrollArea.Default).attrs((p) => {
  return {
    scrollbarVariant: "subtle",
    asFlex: true,
    innerDisplayFlex: true,
    ...p
  };
}).withConfig({
  displayName: "EditorSelector__StyledScrollArea",
  componentId: "sc-8b35be9-3"
})([
  `padding:0;max-height:min(var(--fbc-editor-selector-available-height),360px);width:204px;`,
  `{width:100%;}`
], mediaMobile);
const GroupTitle = styled(DropdownMenu.GroupTitle).withConfig({
  displayName: "EditorSelector__GroupTitle",
  componentId: "sc-8b35be9-4"
})([
  `display:flex;gap:0.5rem;align-items:center;color:rgba(`,
  `,0.7);font-size:0.688rem;padding:0.375rem 0.5rem;width:100%;user-select:none;`,
  `{width:auto;grid-column:span 2;font-size:0.875rem;font-weight:500;}`
], cssVar["color-text-tertiary-rgb"], mediaMobile);
const Group = styled(DropdownMenu.Group).withConfig({
  displayName: "EditorSelector__Group",
  componentId: "sc-8b35be9-5"
})([
  `display:flex;flex-direction:column;padding:0.313rem;&:not(:last-child){border-bottom:1px solid `,
  `;}`,
  `{padding:1rem;display:grid;grid-template-columns:repeat(2,1fr);gap:0.75rem 1rem;}`
], cssVar["color-border-secondary"], mediaMobile);
const ItemLabel = styled.span.withConfig({
  displayName: "EditorSelector__ItemLabel",
  componentId: "sc-8b35be9-6"
})([
  `white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`
]);
const Item = styled(DropdownMenu.Item).attrs(({ value, label, icon, mobileIcon: mobileIconProp, disabled, ...p }) => {
  const mobileIcon = mobileIconProp ?? icon;
  return {
    ...p,
    children: /* @__PURE__ */ jsxs(Fragment, {
      children: [
        p.children,
        icon && /* @__PURE__ */ jsx("span", {
          "data-desktop-icon": true,
          children: icon
        }),
        mobileIcon && /* @__PURE__ */ jsx("span", {
          "data-mobile-icon": true,
          children: mobileIcon
        }),
        label && /* @__PURE__ */ jsx(ItemLabel, {
          children: label
        }),
        isString(disabled) && /* @__PURE__ */ jsx(MenuItemInfoIconTooltip, {
          label: disabled
        })
      ]
    }),
    as: "button",
    ["data-value"]: value,
    ["data-role"]: "item",
    disabled: Boolean(disabled)
  };
}).withConfig({
  displayName: "EditorSelector__Item",
  componentId: "sc-8b35be9-7"
})([
  `padding:0.375rem 0.5rem;font-size:0.875rem;font-weight:500;> svg,[data-desktop-icon],[data-mobile-icon]{color:rgba(`,
  `,0.6);}&[data-more-button]{color:rgba(`,
  `,0.7);> svg{color:rgba(`,
  `,0.7);}}[data-desktop-icon]{display:block;display:contents;}[data-mobile-icon]{display:none;}`,
  `{height:59px;padding:0 1rem;font-size:1rem;border-radius:1rem;background:`,
  `;> svg,[data-desktop-icon],[data-mobile-icon]{color:`,
  `;}&:not(:last-child){border-bottom:none;}[data-desktop-icon]{display:none;}[data-mobile-icon]{display:block;display:contents;}}&[data-delete='true']{color:`,
  `;> svg{color:`,
  `;}}`
], cssVar["color-text-tertiary-rgb"], cssVar["color-text-tertiary-rgb"], cssVar["color-text-tertiary-rgb"], mediaMobile, cssVar["color-bg-secondary"], cssVar["color-text-tertiary"], cssVar["color-app-delete"], cssVar["color-app-delete"]);
const MoreButton = ({ children, label, ...props }) => {
  return /* @__PURE__ */ jsxs(Item, {
    ...props,
    children: [
      /* @__PURE__ */ jsx(IconDesktop16MiscOverflowMenu, {}),
      label && /* @__PURE__ */ jsx(ItemLabel, {
        children: label
      })
    ]
  });
};
const Overlay = styled.div.withConfig({
  displayName: "EditorSelector__Overlay",
  componentId: "sc-8b35be9-8"
})([
  `position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:auto;z-index:125;`
]);
const EditorSelectorBase = ({ children, target, mobileTarget, label, open: openProp, onOpenChange: onOpenChangeProp, renderButton, contentRef: contentRefProp, overlay = false }) => {
  const defaultPortalTarget = getDefaultRadixPortalContainer() ?? document.body;
  const [openState, setOpenState] = useState(openProp ?? false);
  const open = openProp ?? openState;
  const onOpenChange = onOpenChangeProp ?? setOpenState;
  const { isMobileView } = useResponsive();
  const [floatingRef, setFloatingRef] = useState(null);
  const [referenceRef, setReferenceRef] = useState(null);
  const reference = useMemo(() => target ?? referenceRef, [
    target,
    referenceRef
  ]);
  const { floatingStyles, update, refs } = useFloating({
    open,
    strategy: "fixed",
    placement: "bottom-start",
    elements: {
      reference,
      floating: floatingRef
    },
    middleware: [
      inline(),
      offset(6),
      autoPlacement({
        allowedPlacements: [
          "bottom-start",
          "top-start"
        ]
      }),
      size({
        apply({ availableHeight, elements }) {
          const value = `${Math.max(0, availableHeight)}px`;
          elements.floating.style.setProperty("--fbc-editor-selector-available-height", value);
        }
      })
    ]
  });
  const setFloatingUIReference = refs.setReference;
  useEffect(() => {
    setFloatingUIReference(reference);
  }, [
    setFloatingUIReference,
    reference
  ]);
  useEffect(() => {
    if (!open || isMobileView || !floatingRef || !reference) return;
    const unsub = autoUpdate(reference, floatingRef, update);
    return () => unsub();
  }, [
    open,
    update,
    isMobileView,
    floatingRef,
    reference
  ]);
  const innerButtonRef = useRef(null);
  const innerContentRef = useRef(null);
  const contentRef = useMemo(() => mergeRefs([
    innerContentRef,
    contentRefProp
  ]), [
    innerContentRef,
    contentRefProp
  ]);
  const buttonRef = useMemo(() => target ? innerButtonRef : mergeRefs([
    innerButtonRef,
    setReferenceRef
  ]), [
    innerButtonRef,
    target
  ]);
  useOnClickOutside(innerContentRef, () => {
    onOpenChange(false);
  }, {
    exceptions: [
      innerButtonRef
    ]
  });
  const onClickContent = useCallback((e) => {
    if (e.target === e.currentTarget || !(e.target instanceof HTMLElement) || !e.target.closest('[data-role="item"]') || e.target.closest("[data-prevent-default]")) {
      return;
    }
    onOpenChange(false);
  }, [
    onOpenChange
  ]);
  useEffect(() => {
    if (!isMobileView || !open) return;
    innerContentRef.current?.focus();
  }, [
    isMobileView,
    open
  ]);
  const previousOpenRef = useRef(null);
  useEffect(() => {
    if (previousOpenRef.current === open) return;
    previousOpenRef.current = open;
    if (!open) return;
    const style = document.createElement("style");
    style.innerHTML = `
        :root {
          --keyboard-height: 0px;
        }
      `;
    document.head.appendChild(style);
    const timeout = window.setTimeout(() => {
      style.remove();
    }, 1500);
    if (typeof window.visualViewport !== "undefined" && window.visualViewport) {
      const visualViewport = window.visualViewport;
      const remove = () => {
        if (visualViewport.height < window.innerHeight) {
          visualViewport.addEventListener("resize", remove, {
            once: true
          });
          return;
        }
        clearTimeout(timeout);
        setTimeout(() => {
          style.remove();
        }, 100);
      };
      window.visualViewport.addEventListener("resize", remove, {
        once: true
      });
    }
  }, [
    open
  ]);
  const { height, isVisible } = useVirtualKeyboardContext();
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  useEffect(() => {
    if (!isVisible || height <= 0) return;
    setKeyboardHeight(height);
  }, [
    height,
    isVisible
  ]);
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      renderButton && renderButton({
        open,
        onOpenChange,
        props: {
          ref: buttonRef
        }
      }),
      open && /* @__PURE__ */ createPortal(/* @__PURE__ */ jsxs(Fragment, {
        children: [
          overlay && open && /* @__PURE__ */ jsx(Overlay, {
            onClick: () => onOpenChange(false),
            "data-tiptap-toolbar": true
          }),
          isMobileView ? /* @__PURE__ */ jsxs(MobileContent, {
            ref: contentRef,
            onClick: onClickContent,
            "data-tiptap-toolbar": true,
            "data-toolbar": true,
            tabIndex: 0,
            maxHeight: keyboardHeight,
            children: [
              /* @__PURE__ */ jsx(VisuallyHidden, {
                children: label
              }),
              /* @__PURE__ */ jsx(StyledScrollArea, {
                children
              })
            ]
          }) : /* @__PURE__ */ jsx(Anchor, {
            ref: setFloatingRef,
            style: {
              ...floatingStyles
            },
            children: /* @__PURE__ */ jsxs(DesktopContent, {
              ref: contentRef,
              onClick: onClickContent,
              "data-tiptap-toolbar": true,
              "data-toolbar": true,
              children: [
                /* @__PURE__ */ jsx(VisuallyHidden, {
                  children: label
                }),
                /* @__PURE__ */ jsx(StyledScrollArea, {
                  children
                })
              ]
            })
          })
        ]
      }), isMobileView ? mobileTarget ?? defaultPortalTarget : defaultPortalTarget)
    ]
  });
};
const EditorSelector = {
  Group,
  GroupTitle,
  Item,
  MoreButton,
  DesktopContent,
  ScrollArea: StyledScrollArea,
  MobileContent,
  Anchor,
  ItemLabel,
  Base: EditorSelectorBase
};

export { EditorSelector };
//# sourceMappingURL=EditorSelector.js.map
